import { apiRequest } from '@/api/client'
import type { ApiError } from '@/types'

export interface DeployRequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  query?: Record<string, string | number | boolean | undefined | null>
}

export interface DeployFetchOptions {
  method?: string
  body?: BodyInit | null
  signal?: AbortSignal
  query?: DeployRequestOptions['query']
  headers?: Record<string, string>
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000'
const DEPLOY_PREFIX = '/api/guardianforge/deploy'

function getToken(): string | null {
  return localStorage.getItem('auth_token')
}

function handleUnauthenticated(): void {
  localStorage.removeItem('auth_token')
  window.dispatchEvent(new Event('auth:unauthenticated'))
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`
}

function deployPath(path: string, query?: DeployRequestOptions['query']): string {
  let url = `${DEPLOY_PREFIX}${normalizePath(path)}`
  if (query) {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      params.set(key, String(value))
    }
    const qs = params.toString()
    if (qs) url += `?${qs}`
  }
  return url
}

function deployUrl(path: string, query?: DeployRequestOptions['query']): string {
  return `${BASE_URL}${deployPath(path, query)}`
}

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra }
  const token = getToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

class DeployApiError extends Error implements ApiError {
  status: number

  constructor(error: ApiError) {
    super(error.message)
    this.name = 'DeployApiError'
    this.status = error.status
  }
}

function throwApiError(error: ApiError): never {
  throw new DeployApiError(error)
}

async function parseError(response: Response): Promise<ApiError> {
  let message = `HTTP ${response.status}`
  const contentType = response.headers.get('content-type') ?? ''
  try {
    if (contentType.includes('application/json')) {
      const body = await response.json()
      if (body?.detail) {
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail)
      }
    } else {
      const text = await response.text()
      if (text) message = text
    }
  } catch {
    // Keep the generic message.
  }
  return { status: response.status, message }
}

async function ensureOk(response: Response): Promise<Response> {
  if (response.status === 401) {
    handleUnauthenticated()
    throwApiError({ status: 401, message: 'Unauthorized' })
  }
  if (!response.ok) {
    throwApiError(await parseError(response))
  }
  return response
}

export function deploymentApiPath(path: string, query?: DeployRequestOptions['query']): string {
  return deployPath(path, query)
}

export async function deployRequest<T>(
  path: string,
  options: DeployRequestOptions = {},
): Promise<T> {
  return apiRequest<T>(deployPath(path, options.query), {
    method: options.method,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })
}

export async function deployFetchBlob(
  path: string,
  options: DeployFetchOptions = {},
): Promise<Blob> {
  const response = await fetch(deployUrl(path, options.query), {
    method: options.method ?? 'GET',
    body: options.body,
    signal: options.signal,
    headers: authHeaders(options.headers),
  })
  await ensureOk(response)
  return response.blob()
}

export async function deployFetchText(
  path: string,
  options: DeployFetchOptions = {},
): Promise<string> {
  const response = await fetch(deployUrl(path, options.query), {
    method: options.method ?? 'GET',
    body: options.body,
    signal: options.signal,
    headers: authHeaders(options.headers),
  })
  await ensureOk(response)
  return response.text()
}

export async function deployUpload<T>(
  path: string,
  formData: FormData,
  options: Omit<DeployFetchOptions, 'body' | 'headers'> = {},
): Promise<T> {
  const response = await fetch(deployUrl(path, options.query), {
    method: options.method ?? 'POST',
    body: formData,
    signal: options.signal,
    headers: authHeaders(),
  })
  await ensureOk(response)
  return response.json() as Promise<T>
}

export async function downloadDeployBlob(
  path: string,
  filename: string,
): Promise<void> {
  const blob = await deployFetchBlob(path)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
